/**
 * The gate in front of the volunteer page.
 *
 * The mirror of `RequireStaff`, and it exists for the same reason: so that
 * arriving at the wrong door is a redirect rather than a broken screen.
 *
 * A member of staff who lands here is sent back to the portal — unless their
 * account is also linked to a volunteer record, in which case this page is
 * genuinely theirs too. A Parinaam employee who volunteers on a Saturday is one
 * person with one account, and the API already answers `/sessions/mine` for them.
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LoadingState } from '../components/common';
import { useAuth } from '../hooks/useAuth';

export function RequireVolunteer({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingState label="Checking your session…" />;

  if (!user) {
    // No `state.from`. It used to carry the page being guarded, and the
    // sign-in page sent you back to it — which meant the next session
    // inherited the last one’s position. Signing in lands on `/`.
    return <Navigate to="/login" replace />;
  }

  // Staff with no volunteer record of their own have nothing to see here.
  if (user.role !== 'VOLUNTEER' && !user.volunteer_id) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
